/**
 * In-App Messaging Types
 * Types for 1:1 conversations and broadcast messaging
 */

// ============================================================================
// Enums
// ============================================================================

export enum MessageType {
  Text = 0,
  BookingLink = 1,
  Image = 2,
  SystemNotification = 3,
}

export enum BroadcastType {
  DayAppointments = 0,
  Promotion = 1,
  Custom = 2,
}

export enum BroadcastStatus {
  Draft = 0,
  Scheduled = 1,
  Sending = 2,
  Sent = 3,
  Cancelled = 4,
  Failed = 5,
}

// ============================================================================
// User Summary
// ============================================================================

/**
 * Summary information about a user for display in messaging UI
 */
export interface UserSummaryDto {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  profilePictureUrl?: string;
  role: string;
}

/**
 * Summary information about a booking for display in messages
 */
export interface BookingSummaryDto {
  id: number;
  bookingDateTime: string;
  serviceName: string;
  status: string;
}

// ============================================================================
// Conversation Types
// ============================================================================

/**
 * Conversation list item for inbox display
 */
export interface ConversationDto {
  id: number;
  otherParticipant: UserSummaryDto;
  lastMessage?: MessageDto;
  unreadCount: number;
  lastMessageAt?: string;
  isArchived: boolean;
}

/**
 * Request to create a new conversation
 */
export interface CreateConversationDto {
  participantId: string;
  initialMessage?: SendConversationMessageDto;
}

// ============================================================================
// Message Types
// ============================================================================

/**
 * Message details for display
 */
export interface MessageDto {
  id: number;
  conversationId: number;
  senderId: string;
  senderName: string;
  isOwnMessage: boolean;
  content: string;
  type: MessageType;
  sentAt: string;
  readAt?: string;
  relatedBooking?: BookingSummaryDto;
}

/**
 * Request to send a new message in a conversation
 */
export interface SendConversationMessageDto {
  content: string;
  type?: MessageType;
  relatedBookingId?: number;
}

// ============================================================================
// Broadcast Types
// ============================================================================

/**
 * Broadcast details for barber's broadcast history
 */
export interface BroadcastDto {
  id: number;
  type: BroadcastType;
  subject?: string;
  content: string;
  sentAt?: string;
  createdAt: string;
  status: BroadcastStatus;
  recipientCount: number;
  deliveredCount: number;
  readCount: number;
  targetDate?: string;
  scheduledFor?: string;
  /**
   * IANA zone of the barber's zone shop, which reads a bare `scheduledFor` (TZ-1c).
   * Absent from an API that predates TZ-1c, null when unresolved.
   */
  barbershopTimeZone?: string | null;
}

/**
 * Request to create a new broadcast
 */
export interface CreateBroadcastDto {
  type: BroadcastType;
  subject?: string;
  content: string;
  scheduledFor?: string;
  targetDate?: string;
  customerIds?: string[];
}

/**
 * Received broadcast for customer view
 */
export interface ReceivedBroadcastDto {
  id: number;
  fromBarber: UserSummaryDto;
  subject?: string;
  content: string;
  receivedAt: string;
  isRead: boolean;
  relatedBooking?: BookingSummaryDto;
}

/**
 * Customer with appointment info for day-based broadcasts
 */
export interface CustomerAppointmentDto {
  customerId: string;
  customerName: string;
  bookingTime: string;
  serviceName: string;
  bookingId: number;
}

// ============================================================================
// Real-time DTOs (SignalR)
// ============================================================================

/**
 * Real-time typing indicator data
 */
export interface TypingIndicatorDto {
  conversationId: number;
  userId: string;
  isTyping: boolean;
}

/**
 * Real-time read receipt data
 */
export interface MessagesReadDto {
  conversationId: number;
  readBy: string;
  readAt: string;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Paginated response wrapper for messaging APIs
 */
export interface MessagingPaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ============================================================================
// Hook Types
// ============================================================================

export interface UseMessagingHubOptions {
  onMessage?: (message: MessageDto) => void;
  onBroadcast?: (broadcast: ReceivedBroadcastDto) => void;
  onTypingIndicator?: (data: TypingIndicatorDto) => void;
  onMessagesRead?: (data: MessagesReadDto) => void;
  onUnreadCountUpdate?: (count: number) => void;
}

export interface UseMessagingHubReturn {
  isConnected: boolean;
  unreadCount: number;
  joinConversation: (conversationId: number) => Promise<void>;
  leaveConversation: (conversationId: number) => Promise<void>;
  sendTypingIndicator: (conversationId: number, isTyping: boolean) => Promise<void>;
  markAsRead: (conversationId: number) => Promise<void>;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface MessageBubbleProps {
  message: MessageDto;
  showAvatar?: boolean;
  onBookingClick?: (bookingId: number) => void;
}

export interface ConversationListItemProps {
  conversation: ConversationDto;
  isSelected?: boolean;
  onClick?: () => void;
}

export interface MessageComposerProps {
  conversationId: number;
  onSend: (content: string, type?: MessageType, relatedBookingId?: number) => Promise<void>;
  onTyping?: (isTyping: boolean) => void;
  disabled?: boolean;
}

export interface BroadcastComposerProps {
  onSend: (broadcast: CreateBroadcastDto) => Promise<void>;
  initialType?: BroadcastType;
  disabled?: boolean;
}
